import React, { useState, useEffect, useRef } from "react";
import "../styles/DashboardNavbar.css";
import { formatTest , sortNewestFirst} from "../script/formatTestName";
import {
  CircleCheck, Play,
  BookOpenCheck, BarChart3, FileText,
  LogOut, Menu, X, ChevronDown, UserRound,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import Swal from "sweetalert2";
import UserProfile from "./UserProfile";
import Resume from "./Resume";


const TestLabel = ({ name }) => {
  const { title, subtitle, badge } = formatTest(name);
  return (
    <span className="test-label" title={name}>
      <span className="test-label-top">
        <span className="test-label-title">{title}</span>
        {badge && <span className={`test-badge test-badge-${badge.toLowerCase()}`}>{badge}</span>}
      </span>
      {subtitle && <span className="test-label-sub">{subtitle}</span>}
    </span>
  );
};

const DashboardNavbar = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperRef = useRef(null);
  const [data, setData] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const reviewRef = useRef(null);
  const reportRef = useRef(null);
  const userRef = useRef(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [reportList, setReportList] = useState([]);
  const [reviewList, setReviewList] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [resumeOpen, setResumeOpen] = useState(false);

  // Timer used to delay closing hover menus
  const hoverTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  const handleResumeBuilderClick = () => {
    const token = localStorage.getItem("token");
    if (token) {
      setResumeOpen(true);
      setMenuOpen(false); // closes the mobile menu if it was open
    } else {
      alert("Please Login First");
      navigate("/login");
    }
  };

  const [hasCourse, setHasCourse] = useState(
    () => !!localStorage.getItem("selectedCourse")
  );

  useEffect(() => {
    const check = () => setHasCourse(!!localStorage.getItem("selectedCourse"));

    check();
    window.addEventListener("storage", check); // other tabs
    const timer = setInterval(check, 1000); // same tab (Dashboard sets it)

    return () => {
      window.removeEventListener("storage", check);
      clearInterval(timer);
    };
  }, []);

  const student = {
    name:
      data?.name ||
      data?.username ||
      data?.fullName ||
      storedUser?.name ||
      "Guest Scholar",

    email: data?.email || storedUser?.email || "No Email Found",

    uid: data?.userId || data?._id || storedUser?.id || "N/A",

    plan: data?.plan || storedUser?.plan || "free",
  };

  const planType = selectedSub?.planType || student?.plan || "free";
  const isPremium = planType.toLowerCase() === "premium";

  // ---------- Data loaders ----------
  const loadReportTests = async () => {
    const userId = localStorage.getItem("userId");
    setReportLoading(true);
    try {
      const res = await axiosClient.get(`/auth/all-test-names?userId=${userId}`);
      const allTests = res.data?.data || [];
     setReportList(
  sortNewestFirst(
    allTests.filter((t) => !String(t).toUpperCase().includes("DAILY_CHALLENGE"))
  )
);
    } catch (err) {
      console.log("Error loading tests:", err);
      setReportList([]);
    } finally {
      setReportLoading(false);
    }
  };

  const loadReviewTests = async () => {
    const userId = localStorage.getItem("userId");
    setReviewLoading(true);
    try {
      const res = await axiosClient.get(`/auth/all-test-names?userId=${userId}`);
      setReviewList(sortNewestFirst(res.data?.data || []));
    } catch (err) {
      console.log("Error loading tests:", err);
      setReviewList([]);
    } finally {
      setReviewLoading(false);
    }
  };

  // ---------- Hover helpers ----------
  // Open immediately, load data in the background
  const openHoverMenu = (name, loader) => {
    clearTimeout(hoverTimerRef.current);
    if (activeMenu !== name) loader?.();
    setActiveMenu(name);
  };

  // Small delay so moving from the trigger to the dropdown doesn't close it
  const closeHoverMenu = (name) => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setActiveMenu((current) => (current === name ? null : current));
    }, 150);
  };

  // Close report/review/user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const insideReport = reportRef.current && reportRef.current.contains(e.target);
      const insideReview = reviewRef.current && reviewRef.current.contains(e.target);
      const insideUser = userRef.current && userRef.current.contains(e.target);
      if (!insideReport && !insideReview && !insideUser) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNav = (path, options = {}) => {
    if (path) navigate(path, options);
    setMenuOpen(false);
  };

  function enableDevToolsProtection(triggerViolation, video) {
    const handleKeyDown = (e) => {
      if (e.key === "F12") {
        e.preventDefault();
        triggerViolation("Developer Tools Detected 🚫", "You are Eliminated", "DEVTOOLS F12", video);
      }
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "i") {
        e.preventDefault();
        triggerViolation("Developer Tools Detected 🚫", "You are Eliminated", "DEVTOOLS_CTRL_SHIFT_I", video);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }

  // handle quiz
  const handleQuizClick = async () => {
    function clearTestState(userId) {
      localStorage.removeItem(`activeTest_${userId}`);
      localStorage.removeItem(`testName_${userId}`);
      localStorage.removeItem(`answers_${userId}`);
      localStorage.removeItem(`status_${userId}`);
      localStorage.removeItem(`codingStats_${userId}`);
      localStorage.removeItem(`mcqStats_${userId}`);
    }

    const userId = localStorage.getItem("userId");
    const testName = localStorage.getItem(`testName_${userId}`);

    console.log("FINAL TEST NAME:", testName);
    console.log("TEST NAME FROM API:", data?.testName);

    if (!userId || userId === "N/A") {
      navigate("/login");
      return;
    }

    let mediaStream;
    let screenStream;
    let checkCamera = null;
    let monitorInterval = null;
    let quizTerminated = false;
    let video;
    let cameraInterval = null;
    let screenInterval = null;
    let screenVideo;
    let removeDevToolsProtection = null;

    async function sendEvidence(screenImage, cameraImage, eliminationReason = null) {
      const userId = localStorage.getItem("userId");
      const testName = localStorage.getItem(`testName_${userId}`);
      if (!testName) {
        console.log("❌ No testName → skipping evidence only");
        return;
      }
      const token = localStorage.getItem("token");
      const payload = {
        userId: String(data.userId),
        testName: testName,
        elimination: eliminationReason ? String(eliminationReason) : null,
        screenCapture: screenImage ? [screenImage] : [],
        cameraCapture: cameraImage ? [cameraImage] : [],
      };
      console.log("Sending payload:", payload);
      try {
        const res = await axiosClient.post("/auth/saveEvidence", payload);
        console.log("SERVER RESPONSE:", res.data);
        if (eliminationReason) {
          const finishPayload = { userId: String(data.userId) };
          await axiosClient.post("/auth/finish-test", finishPayload, {
            params: { testName },
            headers: { Authorization: `Bearer ${token}` },
          });
          clearTestState(userId);
          localStorage.setItem("startFreshTest", "true");
          console.log("Test finished automatically after violation");
        }
      } catch (error) {
        console.log("API ERROR:", error.response?.data || error.message);
      }
    }
    window.sendEvidence = sendEvidence;

    function captureCameraScreenshot(video) {
      if (!video || video.readyState < 2) {
        console.log("Camera not ready for screenshot");
        return null;
      }
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0);
      return canvas.toDataURL("image/jpeg", 0.6);
    }

    function captureScreenScreenshot() {
      if (!screenVideo || screenVideo.readyState < 2) {
        console.log("Screen video not ready");
        return null;
      }
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = screenVideo.videoWidth;
      canvas.height = screenVideo.videoHeight;
      ctx.drawImage(screenVideo, 0, 0);
      return canvas.toDataURL("image/jpeg", 0.6);
    }

    window.captureCameraScreenshot = captureCameraScreenshot;
    window.captureScreenScreenshot = captureScreenScreenshot;

    function startScreenshotMonitoring() {
      screenInterval = setInterval(() => {
        const screenImage = captureScreenScreenshot();
        if (screenImage) sendEvidence(screenImage, null, null);
        console.log("1 min screen sent");
      }, 60000);

      cameraInterval = setInterval(() => {
        const cameraImage = captureCameraScreenshot(video);
        if (cameraImage) sendEvidence(null, cameraImage, null);
        console.log("Camera captured & sent");
      }, 120000);
    }

    function triggerViolation(title, text, reason, video) {
      if (quizTerminated) return;
      quizTerminated = true;
      if (checkCamera) clearInterval(checkCamera);
      if (monitorInterval) clearInterval(monitorInterval);
      if (cameraInterval) clearInterval(cameraInterval);
      if (screenInterval) clearInterval(screenInterval);
      if (removeDevToolsProtection) removeDevToolsProtection();
      const cameraImage = captureCameraScreenshot(video);
      const screenImage = captureScreenScreenshot();
      sendEvidence(screenImage, cameraImage, reason);
      stopCamera();
      const showCamera = reason === "FACE_NOT_VISIBLE" || reason === "MULTIPLE_FACES";
      Swal.fire({
        icon: "error",
        title: title,
        html: `<p style="font-size:40px; font-weight:800;color:#ef4444">${text}</p>
          ${showCamera && cameraImage ? `
            <div style="margin-top:10px">
              <b>Camera Capture:</b><br/>
              <img src="${cameraImage}" style="width:100%;max-width:300px;margin-top:5px"/>
            </div>` : ""}`,
        allowOutsideClick: false,
        timer: 3000,
        showConfirmButton: false,
        allowEscapeKey: false,
      }).then(() => {
        if (document.fullscreenElement) document.exitFullscreen();
        navigate("/front");
      });
    }

    function stopCamera() {
      quizTerminated = true;
      if (checkCamera) clearInterval(checkCamera);
      if (monitorInterval) clearInterval(monitorInterval);
      if (cameraInterval) clearInterval(cameraInterval);
      if (screenInterval) clearInterval(screenInterval);
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
        mediaStream = null;
      }
      if (screenStream) {
        screenStream.getTracks().forEach((track) => track.stop());
        screenStream = null;
      }
      const video = document.querySelector("video");
      if (video) video.remove();
      if (document.fullscreenElement) document.exitFullscreen();
      console.log("Monitoring stopped, camera stopped");
    }
    window.stopCamera = stopCamera;

    if (screenStream) {
      screenStream.getTracks().forEach((track) => track.stop());
      screenStream = null;
    }
    video = document.querySelector("video");
    if (video) video.remove();
    if (quizTerminated) return;

    const selectedCourse = localStorage.getItem("selectedCourse");

    Swal.fire({
      title: "Select Test Mode",
      html: `
        <p style="margin:4px 0 0;color:#64748b;font-size:18px;">
          ${selectedCourse ? `<b style="color:#6d28d9">${selectedCourse}</b>` : "Choose how you want to practice"}
        </p>

        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:14px;margin-top:20px;">
          <div style="padding:18px 14px;border:2px solid #ddd6fe;border-radius:14px;background:#f5f3ff;text-align:center;">
            <div style="font-size:28px;">📚</div>
            <h3 style="margin:6px 0 4px;color:#6d28d9;font-size:16px;font-weight:800;">Course-Wise</h3>
            <p style="margin:0 0 14px;color:#475569;font-size:13px;">Full course, quick start</p>
            <button id="quiz-non-filtered" type="button"
              style="width:100%;padding:10px 14px;border:none;border-radius:10px;background:#6d28d9;color:#fff;font-size:14px;font-weight:700;cursor:pointer;">
              Start Non-Filtered
            </button>
          </div>

          <div style="padding:18px 14px;border:2px solid #bae6fd;border-radius:14px;background:#f0f9ff;text-align:center;">
            <div style="font-size:28px;">🎯</div>
            <h3 style="margin:6px 0 4px;color:#0369a1;font-size:16px;font-weight:800;">Topic-Wise</h3>
            <p style="margin:0 0 14px;color:#475569;font-size:13px;">Pick your own topics</p>
            <button id="quiz-filtered" type="button"
              style="width:100%;padding:10px 14px;border:none;border-radius:10px;background:#0369a1;color:#fff;font-size:14px;font-weight:700;cursor:pointer;">
              Start Filtered
            </button>
          </div>
        </div>
      `,
      width: 560,
      padding: "24px",
      showConfirmButton: true,
      showDenyButton: true,
      showCancelButton: false,
      showCloseButton: true,
      customClass: { popup: "quiz-mode-popup" },
      didOpen: () => {
        // hide the default Swal buttons; the card buttons trigger them instead
        const c = Swal.getConfirmButton();
        const d = Swal.getDenyButton();
        const actions = Swal.getActions();
        if (c) c.style.display = "none";
        if (d) d.style.display = "none";
        if (actions) actions.style.display = "none";

        document
          .getElementById("quiz-non-filtered")
          ?.addEventListener("click", () => Swal.clickConfirm());
        document
          .getElementById("quiz-filtered")
          ?.addEventListener("click", () => Swal.clickDeny());
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Non-Filtered Quiz: no permission, no monitoring
        navigate("/quiz", { replace: true });
        return;
      }

      if (result.isDenied) {
        document.addEventListener("visibilitychange", function () {
          if (quizTerminated) return;
          if (document.hidden) triggerViolation("Tab Switched 🚫", "You are Eliminated.", "TAB_SWITCH", video);
        });
        navigate("/coding");
      }
    });
  };

  // "Quiz" becomes "Start Test" once a course is selected (detected from localStorage)
  const navItems = [
    hasCourse
      ? { icon: <Play size={18} />, label: "Start Test", onclick: handleQuizClick }
      : { icon: <CircleCheck size={18} />, label: "Quiz", onclick: handleQuizClick },
    { icon: <FileText size={18} />, label: "Resume Builder", onclick: handleResumeBuilderClick },
  ];

  const initials = (student.name || "G")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <nav className="navbarr">
      {/* Logo */}
      <div
        className="navbar-logo"
        title="Home"
        onClick={() => navigate("/")}
        style={{ cursor: "pointer" }}
      >
        <img src="codematrixLogo.png" alt="CodeMatrix" />
      </div>

      {/* Desktop Menu */}
      <div className="navbar-center">
        <ul className="navbar-menu">
          {/* Adaptive — hover to reveal Adaptive Test / Adaptive Report */}
          <li className="nav-pill has-caret hover-dropdown">
            <span className="nav-pill-trigger">
              <span className="nav-pill-icon"><BookOpenCheck size={18} /></span>
              <span>Adaptive</span>
              <ChevronDown size={14} className="caret" />
            </span>

            <div className="report-dropdown hover-dropdown-menu">
              <div className="report-title">Adaptive</div>
              <div
                className="report-item"
                onClick={() => handleNav("/adaptiveform", { state: { fromDashboard: true } })}
              >
                <BookOpenCheck size={15} className="report-item-icon" />
                <span>Adaptive Test</span>
              </div>
              <div
                className="report-item"
                onClick={() => handleNav("/adaptivereport", { state: { fromDashboard: true } })}
              >
                <BarChart3 size={15} className="report-item-icon" />
                <span>Adaptive Report</span>
              </div>
            </div>
          </li>

          {navItems.map(({ icon, label, path, onclick }) => (
            <li
              key={label}
              className="nav-pill"
              onClick={() => {
                if (onclick) onclick();
                else if (path) handleNav(path, { state: { fromDashboard: true } });
              }}
            >
              <span className="nav-pill-icon">{icon}</span>
              <span>{label}</span>
            </li>
          ))}

          {/* Review with hover dropdown */}
          <li
            className="nav-pill has-caret"
            ref={reviewRef}
            onMouseEnter={() => openHoverMenu("review", loadReviewTests)}
            onMouseLeave={() => closeHoverMenu("review")}
          >
            <span
              className="nav-pill-trigger"
              onClick={() =>
                activeMenu === "review"
                  ? setActiveMenu(null)
                  : openHoverMenu("review", loadReviewTests)
              }
            >
              <span className="nav-pill-icon"><FileText size={18} /></span>
              <span>Test Review</span>
              <ChevronDown size={14} className={`caret ${activeMenu === "review" ? "caret-open" : ""}`} />
            </span>

            {activeMenu === "review" && (
              <div className="report-dropdown">
                <div className="report-title">Select a test to review</div>
                {reviewLoading && reviewList.length === 0 ? (
                  <div className="report-empty">Loading...</div>
                ) : reviewList.length > 0 ? (
                  reviewList.map((t, i) => (
                    <div
                      key={i}
                      className="report-item"
                      onClick={() => {
                        localStorage.setItem("reviewTestName", t);
                        setActiveMenu(null);
                        navigate(`/review/${encodeURIComponent(t)}`);
                      }}
                    >
                      <FileText size={15} className="report-item-icon" />
                      <TestLabel name={t} />
                    </div>
                  ))
                ) : (
                  <div className="report-empty">No completed tests yet</div>
                )}
              </div>
            )}
          </li>

          {/* Report with hover dropdown */}
          <li
            className="nav-pill has-caret"
            ref={reportRef}
            onMouseEnter={() => openHoverMenu("report", loadReportTests)}
            onMouseLeave={() => closeHoverMenu("report")}
          >
            <span
              className="nav-pill-trigger"
              onClick={() =>
                activeMenu === "report"
                  ? setActiveMenu(null)
                  : openHoverMenu("report", loadReportTests)
              }
            >
              <span className="nav-pill-icon"><BarChart3 size={18} /></span>
              <span> Test Report</span>
              <ChevronDown size={14} className={`caret ${activeMenu === "report" ? "caret-open" : ""}`} />
            </span>

            {activeMenu === "report" && (
              <div className="report-dropdown">
                <div className="report-title">Select a test report</div>
                {reportLoading && reportList.length === 0 ? (
                  <div className="report-empty">Loading...</div>
                ) : reportList.length > 0 ? (
                  reportList.map((t, i) => (
                    <div
                      key={i}
                      className="report-item"
                      onClick={() => {
                        localStorage.setItem("reportTestName", t);
                        setActiveMenu(null);
                        navigate("/report");
                      }}
                    >
                      <BarChart3 size={15} className="report-item-icon" />
                      <TestLabel name={t} />
                    </div>
                  ))
                ) : (
                  <div className="report-empty">No reports available yet</div>
                )}
              </div>
            )}
          </li>
        </ul>
      </div>

      <div className="nav-rightt">
        {/* Desktop Logout */}
        <button
          onClick={() => {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("selectedCourse");
            localStorage.removeItem("selectedPlan");
            navigate("/login", { replace: true });
          }}
          className="logout-btn"
        >
          <LogOut size={16} /> <span>Logout</span>
        </button>

        {/* User profile */}
        <div className="user-wrap" ref={userRef}>
          <div
            className="user-badge"
            onClick={() => setActiveMenu(activeMenu === "user" ? null : "user")}
          >
            {data?.profileImage ? (
              <img src={data.profileImage} alt="user" className="user-avatar" />
            ) : (
              <div className="user-avatar user-avatar-fallback">{initials}</div>
            )}

            <div className="user-meta">
              <span className="user-name">{student.name}</span>
              <span className={`plan-pill ${isPremium ? "plan-premium" : "plan-free"}`}>
                {planType.toUpperCase()}
              </span>
            </div>

            <ChevronDown size={14} className={`caret user-caret ${activeMenu === "user" ? "caret-open" : ""}`} />
          </div>

          {activeMenu === "user" && (
            <div className="user-dropdown" onMouseDown={(e) => e.stopPropagation()}>
              <div className="user-dropdown-header">
                <div className="user-avatar user-avatar-fallback">{initials}</div>
                <div>
                  <div className="user-dropdown-name">{student.name}</div>
                  <div className="user-dropdown-email">{student.email}</div>
                </div>
              </div>
              <div
                className="dropdown-item"
                onClick={() => {
                  setShowProfilePopup(true);
                  setActiveMenu(null);
                }}
              >
                <UserRound size={16} /> Profile
              </div>

              <div className="dropdown-divider" />

              <div
                className="dropdown-item dropdown-item-danger"
                onClick={() => {
                  localStorage.removeItem("token");
                  localStorage.removeItem("user");
                  localStorage.removeItem("selectedPlan");
                  navigate("/login");
                }}
              >
                <LogOut size={16} /> Logout
              </div>
            </div>
          )}
        </div>

        <UserProfile isOpen={showProfilePopup} onClose={() => setShowProfilePopup(false)} />
      </div>

      {/* Hamburger + Dropdown (mobile only) */}
      <div className="hamburger-wrapper" ref={wrapperRef}>
        <button
          className="hamburger-btn"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        <div className={`mobile-menu${menuOpen ? " open" : ""}`}>
          <div className="mobile-user-card">
            {data?.profileImage ? (
              <img src={data.profileImage} alt="user" className="user-avatar" />
            ) : (
              <div className="user-avatar user-avatar-fallback">{initials}</div>
            )}
            <div className="user-meta">
              <span className="user-name">{student.name}</span>
              <span className={`plan-pill ${isPremium ? "plan-premium" : "plan-free"}`}>
                {planType.toUpperCase()}
              </span>
            </div>
          </div>

          <ul>
            {/* Adaptive accordion for mobile (tap to expand) */}
            <li
              className={activeMenu === "adaptive" ? "mobile-accordion-open" : ""}
              onClick={(e) => {
                e.stopPropagation();
                setActiveMenu(activeMenu === "adaptive" ? null : "adaptive");
              }}
            >
              <span className="mobile-accordion-row">
                <span className="mobile-accordion-label"><BookOpenCheck size={18} /> Adaptive</span>
                <ChevronDown size={16} className={`caret ${activeMenu === "adaptive" ? "caret-open" : ""}`} />
              </span>
              {activeMenu === "adaptive" && (
                <div className="report-dropdown report-dropdown-mobile">
                  <div
                    className="report-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveMenu(null);
                      setMenuOpen(false);
                      handleNav("/adaptiveform", { state: { fromDashboard: true } });
                    }}
                  >
                    <BookOpenCheck size={15} className="report-item-icon" />
                    <span>Adaptive Test</span>
                  </div>
                  <div
                    className="report-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveMenu(null);
                      setMenuOpen(false);
                      handleNav("/adaptivereport", { state: { fromDashboard: true } });
                    }}
                  >
                    <BarChart3 size={15} className="report-item-icon" />
                    <span>Adaptive Report</span>
                  </div>
                </div>
              )}
            </li>

            {navItems.map(({ icon, label, path, onclick }) => (
              <li
                key={label}
                onClick={() => {
                  if (onclick) onclick();
                  else if (path) handleNav(path, { state: { fromDashboard: true } });
                  setMenuOpen(false);
                }}
              >
                {icon}
                {label}
              </li>
            ))}

            {/* Report accordion for mobile */}
            <li
              className={activeMenu === "report" ? "mobile-accordion-open" : ""}
              onClick={(e) => {
                e.stopPropagation();
                if (activeMenu === "report") {
                  setActiveMenu(null);
                } else {
                  loadReportTests();
                  setActiveMenu("report");
                }
              }}
            >
              <span className="mobile-accordion-row">
                <span className="mobile-accordion-label"><BarChart3 size={18} /> Test Report</span>
                <ChevronDown size={16} className={`caret ${activeMenu === "report" ? "caret-open" : ""}`} />
              </span>
              {activeMenu === "report" && (
                <div className="report-dropdown report-dropdown-mobile">
                  {reportLoading && reportList.length === 0 ? (
                    <div className="report-empty">Loading...</div>
                  ) : reportList.length > 0 ? (
                    reportList.map((t, i) => (
                      <div
                        key={i}
                        className="report-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          localStorage.setItem("reportTestName", t);
                          setActiveMenu(null);
                          setMenuOpen(false);
                          navigate("/report");
                        }}
                      >
                        <BarChart3 size={15} className="report-item-icon" />
                        <TestLabel name={t} />
                      </div>
                    ))
                  ) : (
                    <div className="report-empty">No reports available yet</div>
                  )}
                </div>
              )}
            </li>

            {/* Review accordion for mobile */}
            <li
              className={activeMenu === "review" ? "mobile-accordion-open" : ""}
              onClick={(e) => {
                e.stopPropagation();
                if (activeMenu === "review") {
                  setActiveMenu(null);
                } else {
                  loadReviewTests();
                  setActiveMenu("review");
                }
              }}
            >
              <span className="mobile-accordion-row">
                <span className="mobile-accordion-label"><FileText size={18} />Test Review</span>
                <ChevronDown size={16} className={`caret ${activeMenu === "review" ? "caret-open" : ""}`} />
              </span>
              {activeMenu === "review" && (
                <div className="report-dropdown report-dropdown-mobile">
                  {reviewLoading && reviewList.length === 0 ? (
                    <div className="report-empty">Loading...</div>
                  ) : reviewList.length > 0 ? (
                    reviewList.map((t, i) => (
                      <div
                        key={i}
                        className="report-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          localStorage.setItem("reviewTestName", t);
                          setActiveMenu(null);
                          setMenuOpen(false);
                          navigate(`/review/${encodeURIComponent(t)}`);
                        }}
                      >
                        <FileText size={15} className="report-item-icon" />
                        <TestLabel name={t} />
                      </div>
                    ))
                  ) : (
                    <div className="report-empty">No completed tests yet</div>
                  )}
                </div>
              )}
            </li>

            <li
              className="mobile-logout"
              onClick={() => {
                localStorage.removeItem("token");
                localStorage.removeItem("user");
                localStorage.removeItem("selectedCourse");
                localStorage.removeItem("selectedPlan");
                navigate("/login", { replace: true });
              }}
            >
              <LogOut size={18} /> Logout
            </li>
          </ul>
        </div>
      </div>

      <Resume isOpen={resumeOpen} onClose={() => setResumeOpen(false)} />
    </nav>
  );
};

export default DashboardNavbar;